import { useState, useRef, useCallback, useEffect } from "react";
import { Layout } from "../components/Layout";
import { getAuthHeaders as getHeaders } from "../lib/auth";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Upload, FileText, Check, X, AlertTriangle, Loader2, Search,
  Package, Pill, Building2, Calendar, Hash, Layers, ChevronDown, ChevronRight,
  Sparkles, Download, ScanLine, FileUp, Shield, Clock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ─── SEVERITY COLORS ─── */
const SEV = { normal: "text-emerald-400 bg-emerald-500/10", attention: "text-amber-400 bg-amber-500/10", critique: "text-red-400 bg-red-500/10" };

/* ─── FORMAT FCFA ─── */
const fmt = (n) => n ? `${Number(n).toLocaleString("fr-FR")} F` : "—";

export default function ProductScannerPage() {
  const [tab, setTab] = useState("scan"); // scan | import | history
  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="scanner-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
            <ScanLine className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Scanner IA de Produits</h1>
            <p className="text-xs text-slate-500">Scannez, importez et enrichissez vos produits avec l'intelligence artificielle</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="scanner-tabs">
          {[
            { key: "scan", label: "Scanner Produit", icon: Camera },
            { key: "import", label: "Import Fichier Stock", icon: FileUp },
            { key: "history", label: "Historique", icon: Clock },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold transition ${tab === t.key ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "scan" && <ScanProductTab />}
        {tab === "import" && <ImportStockTab />}
        {tab === "history" && <ScanHistoryTab />}
      </div>
    </Layout>
  );
}


/* ═══════════════════════════════════════════════════════
   TAB 1: SCAN SINGLE PRODUCT
   ═══════════════════════════════════════════════════════ */
function ScanProductTab() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editFields, setEditFields] = useState({});
  const fileRef = useRef(null);

  const handleScan = useCallback(async (file) => {
    if (!file) return;
    setScanning(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/scanner/scan-product`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      setEditFields(res.data.product || {});
      toast.success("Produit scanne avec succes !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du scan");
    } finally { setScanning(false); }
  }, []);

  const handleValidate = async () => {
    if (!result?.scan_id) return;
    setSaving(true);
    try {
      await axios.post(`${API}/scanner/validate-product`, {
        scan_id: result.scan_id, product_index: 0,
        name: editFields.name || "", form: editFields.form || "", dosage: editFields.dosage || "",
        lot_number: editFields.lot_number || "", expiry_date: editFields.expiry_date || "",
        laboratory: editFields.laboratory || "", barcode: editFields.barcode || "",
        pharma_class: editFields.pharma_class || "", quantity: Number(editFields.quantity_per_box) || 1,
        unit_price: 0, weight_kg: 0, category: editFields.pharma_class || "Medicament",
      }, { headers: getHeaders() });
      toast.success("Produit enregistre !");
      setResult(null); setEditFields({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur validation");
    } finally { setSaving(false); }
  };

  const updateField = (k, v) => setEditFields(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      {!result && (
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-violet-300 rounded-2xl p-10 text-center cursor-pointer hover:border-violet-500 hover:bg-violet-50/50 transition group"
          data-testid="scan-upload-zone"
        >
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => handleScan(e.target.files[0])} />
          {scanning ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-12 h-12 text-violet-500 animate-spin" />
              <p className="text-sm font-semibold text-violet-600">Analyse IA en cours...</p>
              <p className="text-xs text-slate-500">GPT Vision analyse l'image du produit</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center group-hover:bg-violet-200 transition">
                <Camera className="w-8 h-8 text-violet-500" />
              </div>
              <p className="text-sm font-semibold text-slate-700">Photographiez ou importez un produit</p>
              <p className="text-xs text-slate-500">Photo de la boite, etiquette ou code-barres — JPG, PNG ou PDF</p>
            </div>
          )}
        </div>
      )}

      {/* Results + Edit Form */}
      {result && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="scan-result">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              <h3 className="text-sm font-bold text-slate-800">Resultat de l'analyse IA</h3>
              {editFields.confidence && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold">
                  Confiance: {Math.round(editFields.confidence * 100)}%
                </span>
              )}
            </div>
            <button onClick={() => { setResult(null); setEditFields({}); }} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { key: "name", label: "Nom du produit", icon: Package },
              { key: "dci", label: "DCI (Molecule)", icon: Pill },
              { key: "pharma_class", label: "Classe pharmacotherapeutique", icon: Layers },
              { key: "form", label: "Forme galenique", icon: FileText },
              { key: "dosage", label: "Dosage", icon: Hash },
              { key: "laboratory", label: "Laboratoire", icon: Building2 },
              { key: "lot_number", label: "Numero de lot", icon: Hash },
              { key: "expiry_date", label: "Date de peremption", icon: Calendar },
              { key: "barcode", label: "Code-barres", icon: ScanLine },
              { key: "indication", label: "Indication therapeutique", icon: Search },
              { key: "storage_conditions", label: "Conservation", icon: Shield },
              { key: "country_origin", label: "Pays d'origine", icon: Building2 },
            ].map(f => (
              <div key={f.key} className="flex items-center gap-2">
                <f.icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <div className="flex-1">
                  <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{f.label}</label>
                  <input
                    value={editFields[f.key] || ""}
                    onChange={e => updateField(f.key, e.target.value)}
                    className="w-full mt-0.5 px-2 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:border-violet-400 focus:ring-1 focus:ring-violet-200 outline-none"
                    data-testid={`field-${f.key}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-slate-100 flex gap-2 justify-end">
            <button onClick={() => { setResult(null); setEditFields({}); }} className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition">
              Annuler
            </button>
            <button onClick={handleValidate} disabled={saving || !editFields.name} data-testid="validate-product-btn"
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-lg transition disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Valider et enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   TAB 2: IMPORT STOCK FILE
   ═══════════════════════════════════════════════════════ */
function ImportStockTab() {
  const [importing, setImporting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [savingBatch, setSavingBatch] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const fileRef = useRef(null);

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setData(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/scanner/import-stock-file`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      setData(res.data);
      toast.success(`${res.data.total_items} produits extraits !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur import");
    } finally { setImporting(false); }
  };

  const handleEnrich = async () => {
    if (!data?.scan_id) return;
    setEnriching(true);
    try {
      const res = await axios.post(`${API}/scanner/enrich-products`, { scan_id: data.scan_id }, { headers: getHeaders() });
      setData(prev => ({ ...prev, products: res.data.products, status: "pending_validation" }));
      toast.success(`${res.data.enriched_count} produits enrichis par l'IA !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur enrichissement");
    } finally { setEnriching(false); }
  };

  const handleSaveBatch = async () => {
    if (!data?.scan_id) return;
    setSavingBatch(true);
    try {
      const fd = new FormData();
      fd.append("scan_id", data.scan_id);
      const res = await axios.post(`${API}/scanner/validate-batch`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.saved_count} produits enregistres !`);
      setData(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur enregistrement");
    } finally { setSavingBatch(false); }
  };

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      {!data && (
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-cyan-300 rounded-2xl p-10 text-center cursor-pointer hover:border-cyan-500 hover:bg-cyan-50/50 transition group"
          data-testid="import-upload-zone"
        >
          <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={e => handleImport(e.target.files[0])} />
          {importing ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-12 h-12 text-cyan-500 animate-spin" />
              <p className="text-sm font-semibold text-cyan-600">Extraction OCR en cours...</p>
              <p className="text-xs text-slate-500">GPT Vision analyse le document de correction de stock</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-2xl bg-cyan-100 flex items-center justify-center group-hover:bg-cyan-200 transition">
                <FileUp className="w-8 h-8 text-cyan-500" />
              </div>
              <p className="text-sm font-semibold text-slate-700">Importez un fichier de correction de stock</p>
              <p className="text-xs text-slate-500">Format Logimatique Mediciel ou similaire — PDF ou image</p>
            </div>
          )}
        </div>
      )}

      {/* Imported Data */}
      {data && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="import-result">
          {/* Header */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-cyan-500" />
                  {data.pharmacy_name || "Fichier de stock"} — {data.correction_reason || "Correction"}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">{data.total_items} produits extraits</p>
              </div>
              <div className="flex gap-2">
                {data.status === "pending_enrichment" && (
                  <button onClick={handleEnrich} disabled={enriching} data-testid="enrich-btn"
                    className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition disabled:opacity-50">
                    {enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    Enrichir avec l'IA
                  </button>
                )}
                <button onClick={handleSaveBatch} disabled={savingBatch} data-testid="save-batch-btn"
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition disabled:opacity-50">
                  {savingBatch ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Tout enregistrer
                </button>
                <button onClick={() => setData(null)} className="px-3 py-2 text-xs text-slate-500 hover:bg-slate-100 rounded-lg">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Products Table */}
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-xs" data-testid="import-products-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">#</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Produit</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Classe</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Qte</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">Prix HT</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Peremption</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Alerte</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Detail</th>
                </tr>
              </thead>
              <tbody>
                {(data.products || []).map((p, i) => (
                  <ProductRow key={i} p={p} i={i} expanded={expanded === i} onExpand={() => setExpanded(expanded === i ? null : i)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductRow({ p, i, expanded, onExpand }) {
  const alertColor = SEV[p.alert_level] || SEV.normal;
  return (
    <>
      <tr className={`border-b border-slate-50 hover:bg-slate-50/50 ${p.validated ? "bg-emerald-50/30" : ""}`}>
        <td className="px-3 py-2 text-slate-400">{i + 1}</td>
        <td className="px-3 py-2">
          <p className="font-semibold text-slate-800">{p.name}</p>
          {p.dci && <p className="text-[10px] text-slate-500">{p.dci}</p>}
        </td>
        <td className="px-3 py-2 text-slate-600">{p.pharma_class || "—"}</td>
        <td className="px-3 py-2 text-center font-medium">{p.quantity || "—"}</td>
        <td className="px-3 py-2 text-right">{fmt(p.unit_price_ht)}</td>
        <td className="px-3 py-2">
          {p.expiry_date || p.estimated_expiry ? (
            <span className="text-slate-700">{p.expiry_date || <span className="text-amber-600 italic">{p.estimated_expiry} (IA)</span>}</span>
          ) : <span className="text-red-400 italic">Non renseignee</span>}
        </td>
        <td className="px-3 py-2 text-center">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${alertColor}`}>{p.alert_level || "normal"}</span>
        </td>
        <td className="px-3 py-2 text-center">
          <button onClick={onExpand} className="text-slate-400 hover:text-violet-600">
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="bg-violet-50/30 px-6 py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div><span className="text-slate-500">Labo:</span> <span className="font-medium text-slate-700">{p.laboratory || "—"}</span></div>
              <div><span className="text-slate-500">Forme:</span> <span className="font-medium text-slate-700">{p.form || "—"}</span></div>
              <div><span className="text-slate-500">Dosage:</span> <span className="font-medium text-slate-700">{p.dosage || "—"}</span></div>
              <div><span className="text-slate-500">Lot:</span> <span className="font-medium text-slate-700">{p.lot_number || "—"}</span></div>
              <div><span className="text-slate-500">Duree de vie:</span> <span className="font-medium text-slate-700">{p.typical_shelf_life_months ? `${p.typical_shelf_life_months} mois` : "—"}</span></div>
              <div><span className="text-slate-500">Code:</span> <span className="font-medium text-slate-700">{p.product_code || "—"}</span></div>
              <div><span className="text-slate-500">TTC:</span> <span className="font-medium text-slate-700">{fmt(p.total_ttc)}</span></div>
              <div><span className="text-slate-500">Taxe:</span> <span className="font-medium text-slate-700">{p.tax_rate != null ? `${p.tax_rate}%` : "—"}</span></div>
              {p.notes && <div className="col-span-4"><span className="text-slate-500">Notes IA:</span> <span className="font-medium text-amber-700">{p.notes}</span></div>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


/* ═══════════════════════════════════════════════════════
   TAB 3: SCAN HISTORY
   ═══════════════════════════════════════════════════════ */
function ScanHistoryTab() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/scanner/scans?limit=30`, { headers: getHeaders() });
        setScans(res.data.scans || []);
      } catch (err) { 
        console.error("Error loading scan history:", err);
        // Don't show error toast for empty results
      }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="scan-history">
      <div className="p-4 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-800">Historique des scans</h3>
      </div>
      {scans.length === 0 ? (
        <div className="p-10 text-center text-sm text-slate-400">Aucun scan effectue</div>
      ) : (
        <div className="divide-y divide-slate-50">
          {scans.map(s => (
            <div key={s.id} className="p-4 flex items-center gap-4 hover:bg-slate-50/50 transition" data-testid={`scan-${s.id}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.type === "single_product" ? "bg-violet-100" : "bg-cyan-100"}`}>
                {s.type === "single_product" ? <Camera className="w-4 h-4 text-violet-500" /> : <FileUp className="w-4 h-4 text-cyan-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{s.source_filename || "Scan"}</p>
                <p className="text-[10px] text-slate-500">{s.type === "single_product" ? "Scan produit" : `Import stock — ${s.total_items || 0} produits`} · {s.status}</p>
              </div>
              <div className="text-[10px] text-slate-400">{new Date(s.created_at).toLocaleDateString("fr-FR")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
